import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpRight,
  Download,
  FileText,
  Library,
  Mail,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toolkitApi } from "@/api/toolkit";
import { PERMISSIONS } from "@/auth/permissions";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Spinner,
  Tabs,
} from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  ContactModal,
  type ContactTarget,
  DeleteContactModal,
} from "@/components/toolkit/ContactModals";
import {
  DeleteDocumentModal,
  DocumentModal,
  type DocumentTarget,
} from "@/components/toolkit/DocumentModals";
import {
  fileExtension,
  formatDate,
  formatFileSize,
} from "@/components/toolkit/format";
import { OwuLibraryTab } from "@/components/toolkit/OwuLibraryTab";
import { SearchTab } from "@/components/toolkit/SearchTab";
import { TerminationTab } from "@/components/toolkit/TerminationTab";
import { useInterfaceLayout } from "@/hooks/useInterfaceLayout";
import { useHasPermission } from "@/hooks/usePermission";
import { useInsurerContacts, useToolkitDocuments } from "@/hooks/useToolkit";
import type { InsurerContact, ToolkitDocument } from "@/types/toolkit";

type HandbookTab = "contacts" | "documents" | "owu" | "search" | "termination";

const TABS = [
  {
    id: "termination",
    label: "Generator wypowiedzenia",
    icon: <FileText size={16} />,
  },
  {
    id: "contacts",
    label: "Kontakty do towarzystw",
    icon: <Users size={16} />,
  },
  {
    id: "documents",
    label: "Dokumenty do pobrania",
    icon: <FileText size={16} />,
  },
  {
    id: "owu",
    label: "Biblioteka OWU",
    icon: <Library size={16} />,
  },
  {
    id: "search",
    label: "Zapytaj Podręcznik",
    icon: <Search size={16} />,
  },
];

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded-md p-1.5 text-faint hover:bg-muted hover:text-main focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-adara-500"
    >
      {children}
    </button>
  );
}

function matches(text: string | null, query: string) {
  return !!text && text.toLowerCase().includes(query);
}

// ============================
// Contacts
// ============================

function ContactsTab() {
  const { data, isLoading } = useInsurerContacts();
  const canCreate = useHasPermission(PERMISSIONS.TOOLKIT.CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.TOOLKIT.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.TOOLKIT.DELETE);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<ContactTarget | null>(null);
  const [deleting, setDeleting] = useState<InsurerContact | null>(null);

  const insurers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (
      (data ?? [])
        .map((insurer) => {
          if (!q || matches(insurer.insurerName, q)) {
            return insurer;
          }
          const contacts = insurer.contacts.filter(
            (c) =>
              matches(c.fullName, q) ||
              matches(c.position, q) ||
              matches(c.email, q) ||
              matches(c.phone, q),
          );
          return { ...insurer, contacts };
        })
        .filter(
          (insurer) =>
            insurer.contacts.length > 0 ||
            // Editors see empty insurers so they can add the first contact.
            (canCreate && (!q || matches(insurer.insurerName, q))),
        )
        // Insurers with contacts first; the sort is stable, so names stay in order.
        .sort(
          (a, b) =>
            Number(b.contacts.length > 0) - Number(a.contacts.length > 0),
        )
    );
  }, [data, query, canCreate]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
        placeholder="Szukaj towarzystwa, osoby, telefonu…"
        leftIcon={<Search size={16} aria-hidden />}
        aria-label="Szukaj w kontaktach"
      />

      {insurers.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={24} aria-hidden />}
            title={query ? "Brak wyników" : "Brak kontaktów"}
            description={
              query
                ? "Nic nie pasuje do wyszukiwania."
                : "Kontakty do managerów w towarzystwach pojawią się tutaj, gdy wspólnicy je dodadzą."
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {insurers.map((insurer) => (
            <Card key={insurer.insurerId} padding="none">
              <div className="flex items-center justify-between gap-2 border-b border-edge-soft px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-main">
                    {insurer.insurerName}
                  </h3>
                  {!insurer.isActive && <Badge>nieaktywne</Badge>}
                </div>
                {canCreate && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Plus size={14} aria-hidden />}
                    aria-label={`Dodaj kontakt — ${insurer.insurerName}`}
                    onClick={() => {
                      setEditing({
                        insurerId: insurer.insurerId,
                        insurerName: insurer.insurerName,
                        contact: null,
                      });
                    }}
                  >
                    Dodaj
                  </Button>
                )}
              </div>
              {insurer.contacts.length === 0 ? (
                <p className="px-4 py-3 text-sm text-faint">Brak kontaktów.</p>
              ) : (
                <ul className="divide-y divide-edge-soft">
                  {insurer.contacts.map((c) => (
                    <li key={c.id} className="flex gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-sm font-medium text-main">
                          {c.fullName}
                          {c.position && (
                            <span className="font-normal text-dim">
                              {" "}
                              · {c.position}
                            </span>
                          )}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                          {c.phone && (
                            <a
                              href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}
                              className="inline-flex items-center gap-1.5 text-adara-700 hover:underline dark:text-adara-300"
                            >
                              <Phone size={14} aria-hidden />
                              {c.phone}
                            </a>
                          )}
                          {c.email && (
                            <a
                              href={`mailto:${c.email}`}
                              className="inline-flex min-w-0 items-center gap-1.5 break-all text-adara-700 hover:underline dark:text-adara-300"
                            >
                              <Mail
                                size={14}
                                aria-hidden
                                className="shrink-0"
                              />
                              {c.email}
                            </a>
                          )}
                        </div>
                        {c.note && <p className="text-xs text-dim">{c.note}</p>}
                      </div>
                      {(canUpdate || canDelete) && (
                        <div className="flex shrink-0 items-start">
                          {canUpdate && (
                            <IconButton
                              label={`Edytuj ${c.fullName}`}
                              onClick={() => {
                                setEditing({
                                  insurerId: insurer.insurerId,
                                  insurerName: insurer.insurerName,
                                  contact: c,
                                });
                              }}
                            >
                              <Pencil size={16} aria-hidden />
                            </IconButton>
                          )}
                          {canDelete && (
                            <IconButton
                              label={`Usuń ${c.fullName}`}
                              onClick={() => {
                                setDeleting(c);
                              }}
                            >
                              <Trash2 size={16} aria-hidden />
                            </IconButton>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      <ContactModal
        target={editing}
        onClose={() => {
          setEditing(null);
        }}
      />
      <DeleteContactModal
        contact={deleting}
        onClose={() => {
          setDeleting(null);
        }}
      />
    </div>
  );
}

// ============================
// Documents
// ============================

async function download(document: ToolkitDocument) {
  try {
    const blob = await toolkitApi.downloadDocument(document.id);
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = document.fileName;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    toast.error(
      (error as { message?: string }).message ??
        "Nie udało się pobrać dokumentu",
    );
  }
}

function DocumentsTab({
  onAdd,
  onEdit,
  onDelete,
}: {
  onAdd: () => void;
  onEdit: (d: ToolkitDocument) => void;
  onDelete: (d: ToolkitDocument) => void;
}) {
  const { data, isLoading } = useToolkitDocuments();
  const canCreate = useHasPermission(PERMISSIONS.TOOLKIT.CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.TOOLKIT.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.TOOLKIT.DELETE);

  const groups = useMemo(() => {
    const byCategory = new Map<string, ToolkitDocument[]>();
    for (const d of data ?? []) {
      byCategory.set(d.category, [...(byCategory.get(d.category) ?? []), d]);
    }
    return [...byCategory.entries()].sort(([a], [b]) =>
      a.localeCompare(b, "pl"),
    );
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<FileText size={24} aria-hidden />}
          title="Brak dokumentów"
          description="Wzory zgód RODO, APK, wypowiedzeń i inne druki pojawią się tutaj."
          action={
            canCreate && (
              <Button leftIcon={<Plus size={16} aria-hidden />} onClick={onAdd}>
                Dodaj dokument
              </Button>
            )
          }
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(([category, documents]) => (
        <section key={category} className="space-y-2">
          <h2 className="text-xs font-semibold text-dim">{category}</h2>
          <Card padding="none">
            <ul className="divide-y divide-edge-soft">
              {documents.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-[10px] font-bold text-dim">
                      {fileExtension(d.fileName)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-main">{d.title}</p>
                      {d.description && (
                        <p className="text-sm text-dim">{d.description}</p>
                      )}
                      <p className="text-xs text-faint">
                        {formatFileSize(d.sizeBytes)} · aktualizacja{" "}
                        {formatDate(d.fileUpdatedAt)}
                        {d.version > 1 && ` · wersja ${d.version}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 self-end sm:self-auto">
                    {canUpdate && (
                      <IconButton
                        label={`Edytuj ${d.title}`}
                        onClick={() => {
                          onEdit(d);
                        }}
                      >
                        <Pencil size={16} aria-hidden />
                      </IconButton>
                    )}
                    {canDelete && (
                      <IconButton
                        label={`Usuń ${d.title}`}
                        onClick={() => {
                          onDelete(d);
                        }}
                      >
                        <Trash2 size={16} aria-hidden />
                      </IconButton>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Download size={14} aria-hidden />}
                      onClick={() => {
                        void download(d);
                      }}
                    >
                      Pobierz
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ))}
    </div>
  );
}

// ============================
// Page
// ============================

export function AgentHandbookPage() {
  const { layout } = useInterfaceLayout();
  const studio = layout === "studio";
  const { tool } = useSearch({ from: "/authenticated/handbook" });
  const navigate = useNavigate();
  const tab = tool ?? (studio ? null : "contacts");
  const setTab = (next: HandbookTab | null) => {
    void navigate({ to: "/handbook", search: { tool: next ?? undefined } });
  };
  const canCreate = useHasPermission(PERMISSIONS.TOOLKIT.CREATE);
  const { data: documents } = useToolkitDocuments();
  const [editingDocument, setEditingDocument] = useState<DocumentTarget | null>(
    null,
  );
  const [deletingDocument, setDeletingDocument] =
    useState<ToolkitDocument | null>(null);

  const categories = useMemo(
    () => [...new Set((documents ?? []).map((d) => d.category))],
    [documents],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Podręcznik Agenta"
        description="Kontakty do managerów w towarzystwach, dokumenty do pobrania, biblioteka OWU i wyszukiwarka."
        actions={
          tab === "documents" &&
          canCreate && (
            <Button
              leftIcon={<Plus size={16} aria-hidden />}
              onClick={() => {
                setEditingDocument({ document: null });
              }}
            >
              Dodaj dokument
            </Button>
          )
        }
      />

      {studio && tab && (
        <div className="studio-handbook-heading">
          <button
            type="button"
            className="studio-back"
            onClick={() => setTab(null)}
          >
            <ArrowLeft size={15} aria-hidden />
            Podręcznik Agenta
          </button>
        </div>
      )}
      {studio && !tab && (
        <div className="studio-handbook-tools">
          {[
            {
              id: "termination",
              title: "Generator wypowiedzeń",
              description:
                "Klient, dane polisy i PDF do podpisu. Z podglądem dokumentu na żywo.",
              icon: FileText,
            },
            {
              id: "contacts",
              title: "Kontakty do towarzystw",
              description: "Opiekunowie, telefony i adresy e-mail.",
              icon: Users,
            },
            {
              id: "documents",
              title: "Dokumenty i formularze",
              description: "Aktualne wzory zgód, formularzy i dokumentów.",
              icon: Download,
            },
            {
              id: "owu",
              title: "Biblioteka OWU",
              description: "Dokumenty towarzystw według produktów i wersji.",
              icon: Library,
            },
            {
              id: "search",
              title: "Zapytaj Podręcznik",
              description: "Znajdź odpowiedź w dostępnych materiałach.",
              icon: Search,
            },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className="studio-handbook-tool"
              onClick={() => setTab(item.id as HandbookTab)}
            >
              <item.icon size={23} aria-hidden />
              <span>
                <strong>{item.title}</strong>
                <small>{item.description}</small>
              </span>
              <ArrowUpRight size={19} aria-hidden />
            </button>
          ))}
        </div>
      )}
      {!studio && (
        <Tabs
          tabs={TABS}
          activeTab={tab ?? "contacts"}
          onChange={(id) => {
            setTab(id as HandbookTab);
          }}
        />
      )}

      {tab === "termination" && <TerminationTab studio={studio} />}
      {tab === "contacts" && <ContactsTab />}
      {tab === "owu" && <OwuLibraryTab />}
      {tab === "search" && <SearchTab />}
      {tab === "documents" && (
        <DocumentsTab
          onAdd={() => {
            setEditingDocument({ document: null });
          }}
          onEdit={(document) => {
            setEditingDocument({ document });
          }}
          onDelete={setDeletingDocument}
        />
      )}

      <DocumentModal
        target={editingDocument}
        categories={categories}
        onClose={() => {
          setEditingDocument(null);
        }}
      />
      <DeleteDocumentModal
        document={deletingDocument}
        onClose={() => {
          setDeletingDocument(null);
        }}
      />
    </div>
  );
}
